import React, { ReactElement } from 'react';

// material-ui
import { Collapse, Fade, Box, Grow, Slide, Zoom } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';

// ==============================|| TRANSITIONS ||============================== //

type TransitionPosition = 'top-right' | 'top' | 'bottom-left' | 'bottom-right' | 'bottom' | 'top-left';
type TransitionType = 'grow' | 'collapse' | 'fade' | 'slide' | 'zoom';

interface TSProps extends Omit<TransitionProps, 'children'> {
    children?: ReactElement;
    position?: TransitionPosition;
    sx?: SxProps<Theme>;
    type?: TransitionType;
    direction?: 'up' | 'right' | 'left' | 'down';
}

const Transitions = React.forwardRef<HTMLDivElement, TSProps>(
    ({ children, position = 'top-left', sx, type = 'grow', direction = 'up', ...others }, ref) => {
        let positionSX: { transformOrigin: string } = {
            transformOrigin: '0 0 0'
        };

        switch (position) {
            case 'top-right':
                positionSX = {
                    transformOrigin: 'top right'
                };
                break;
            case 'top':
                positionSX = {
                    transformOrigin: 'top'
                };
                break;
            case 'bottom-left':
                positionSX = {
                    transformOrigin: 'bottom left'
                };
                break;
            case 'bottom-right':
                positionSX = {
                    transformOrigin: 'bottom right'
                };
                break;
            case 'bottom':
                positionSX = {
                    transformOrigin: 'bottom'
                };
                break;
            case 'top-left':
            default:
                positionSX = {
                    transformOrigin: '0 0 0'
                };
                break;
        }

        return (
            <Box ref={ref}>
                {type === 'grow' && (
                    <Grow {...others}>
                        <Box sx={positionSX}>{children}</Box>
                    </Grow>
                )}
                {type === 'collapse' && (
                    <Collapse {...others} sx={positionSX}>
                        {children}
                    </Collapse>
                )}
                {type === 'fade' && (
                    <Fade
                        {...others}
                        timeout={{
                            appear: 500,
                            enter: 600,
                            exit: 400
                        }}
                    >
                        <Box sx={positionSX}>{children}</Box>
                    </Fade>
                )}
                {type === 'slide' && (
                    <Slide
                        {...others}
                        timeout={{
                            appear: 0,
                            enter: 400,
                            exit: 200
                        }}
                        direction={direction}
                    >
                        <Box sx={positionSX}>{children}</Box>
                    </Slide>
                )}
                {type === 'zoom' && (
                    <Zoom {...others}>
                        <Box sx={positionSX}>{children}</Box>
                    </Zoom>
                )}
            </Box>
        );
    }
);

export default Transitions;
